import React, { useEffect, useState } from "react";
import { RouteComponentProps, useLocation } from "react-router";
import {
  Grid,
  Typography,
  Paper,
  Button,
  makeStyles,
  createStyles,
  Theme,
  Card,
  CardHeader,
  Divider,
  CardContent
} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { PictureAsPdf } from "@material-ui/icons";
import TrTable from "../shared/controls/TrTable";
import TrTableHeadMetadata from "../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../models/shared/TrTableBodyData";
import FinancingRequestDetails from "../../models/financing-request/FinancingRequestDetails";
import FinancingRequestStudentDetails from "../../models/financing-request/FinancingRequestStudentDetails";
import FinancingRequestApiClient from "../../api/FinancingRequestApiClient";
import useStudentDataInfo from "../../hooks/student-data-info/useStudentDataInfo";
import { financingRequestPrintUrl } from "../../urls";
import { SchoolCarrierDetails } from "../../models/financing-request/SchoolCarrierDetails";
import { VehicleType } from "../../models/vehicle-type/VehicleType";
import useCodeBook from "../../hooks/codebook/useCodeBook";
import { AuthorizedCarrierDetails } from "../../models/financing-request/AuthorizedCarrierDetails";
import { numberFormat, currencyFormat } from "../../utilities/utilities";
import AnnualAdministration from "../../models/admin/annual-administration/AnnualAdministration";
import PaymentRequestApiClient from "../../api/PaymentRequestApiClient";
import DecisionDocument from "../../models/financing-request/DecisionDocument";
import { Link } from "react-router-dom";
import DocumentApiClient from "../../api/DocumentApiClient";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';


interface MatchParams {
  id: string;
}

interface Props extends RouteComponentProps<MatchParams> { }

export default function FinancingRequestDetailsPage({ match }: Props) {
  const classes = useStyles();
  const location = useLocation();
  const { id } = match.params;
  const apiClient = new FinancingRequestApiClient();
  const apiPaymentClient = new PaymentRequestApiClient();
  const documentApiClient = new DocumentApiClient();

  const [annualAdministration, setAnualAdministration] = useState<
    AnnualAdministration
  >({} as AnnualAdministration);


  const [financingRequest, setFinancingRequest] = useState<
    FinancingRequestDetails
  >({} as FinancingRequestDetails);

  useEffect(() => {
    async function getFinancingRequest() {
      const request = await apiClient.getDetails(id);
      const pricingRequest = await apiPaymentClient.getAnualPricing();

      setAnualAdministration(pricingRequest.filter((price) => {
        return price.schoolYearName === request.schoolYear;
      })[0]);

      setFinancingRequest(request);
    }

    getFinancingRequest();
  }, []);

  const {
    getTransportOptionInfoByStudentId,
    getFoodOptionInfoByStudentId,
    getTeachingAidOptionInfoByStudentId
  } = useStudentDataInfo({ financingRequestId: financingRequest.id });

  const { vehicleTypeSelectItems } = useCodeBook({
    getVehicleType: true
  });

  const handleDownload = async () => {
    await apiClient.exportDetails(id);
  };

  const handleDownloadDocumentClick = async (id: number, type: 'decision' | 'carrier' | 'student' | 'authorized', fileName: string) => {
    if (type === 'decision') {
      let response = await documentApiClient.downloadDecisionPdf(id, fileName);
    } else if (type === 'carrier') {
      let response = await documentApiClient.downloadSchoolCarrierPdf(id, fileName);
    } else if (type === 'student') {
      let response = await documentApiClient.downloadStudentPdf(id, fileName);
    } else if (type === 'authorized') {
      let response = await documentApiClient.downloadAuthorizedCarrierPdf(id, fileName);
    }
  };

  const decisionTableHeadMetadata: TrTableHeadMetadata<DecisionDocument>[] = [
    {
      id: "fileName",
      label: "Naziv odluke",
      dataFunc: (d: DecisionDocument) => (
        <Typography variant="body2">Odluka o potrebi osiguravanja prilagođenog prijevoza</Typography>
      )
    },
    {
      id: "documents",
      label: "Dokumentacija",
      dataFunc: (d: DecisionDocument) => (
        <div style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          {location.pathname === financingRequestPrintUrl(id) && d && (
            <p>{d.fileName}</p>
          )
          }
          {location.pathname !== financingRequestPrintUrl(id) && d && (
            <Link to={{}} onClick={() => handleDownloadDocumentClick(d.id || 0, "decision", d.fileName || "document.pdf")}>
              {d.fileName}
            </Link>
          )
          }
        </div>
      )
    }
  ];

  const decisionTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: [financingRequest.decisionDocument]
  };

  const schoolCarrierTableHeadMetadata: TrTableHeadMetadata<
    SchoolCarrierDetails
  >[] = [
      {
        id: "vehicleType",
        label: "Vrsta vozila",
        dataFunc: (sc: SchoolCarrierDetails) => {
          if (sc.vehicleType && vehicleTypeSelectItems) {
            const vehicleType = vehicleTypeSelectItems.find(
              vt => vt.value == sc.vehicleType
            );
            if (vehicleType) return vehicleType.text;
          }
          return "";
        }
      },
      {
        id: "registrationPlate",
        label: "Registracijske oznake",
        dataName: "registrationPlate"
      },
      {
        id: "dailyMileageTotal",
        label: "Ukupna dnevna kilometraža",
        dataName: "dailyMileageTotal",
        dataFunc: (sc: SchoolCarrierDetails) => {
          return numberFormat(sc.dailyMileageTotal || 0, "km", 4);
        }
      },
      {
        id: "numberOfStudents",
        label: "Broj učenika na zahtjevu",
        dataName: "numberOfStudents"
      },
      {
        id: "numberOfStudentsTotal",
        label: "Broj učenika ukupno",
        dataName: "numberOfStudentsTotal"
      },
      {
        id: "documents",
        label: "Dokumentacija",
        dataFunc: (sc: SchoolCarrierDetails) => (
          <div style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            {location.pathname === financingRequestPrintUrl(id) && sc.uploadedDocuments && sc.uploadedDocuments.map((file, index) => (
              <p>{file.fileName}</p>
            ))}
            {location.pathname !== financingRequestPrintUrl(id) && sc.uploadedDocuments && sc.uploadedDocuments.map((file, index) => (
              <div key={index} style={{ justifyContent: 'space-between', alignItems: 'center', display: 'grid' }}>
                <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "carrier", file.fileName || "document.pdf")}>
                  {file.fileName}
                </Link>
              </div>
            ))}
          </div>
        )
      }
    ];

  const schoolCarrierTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: (financingRequest && financingRequest.schoolCarriers) || []
  };

  const authorizedCarrierTableHeadMetadata: TrTableHeadMetadata<
    AuthorizedCarrierDetails
  >[] = [
      {
        id: "name",
        label: "Naziv prijevoznika",
        dataName: "name"
      },
      {
        id: "dailyMileage",
        label: "Ukupna dnevna kilometraža",
        dataName: "dailyMileage",
        dataFunc: (ac: AuthorizedCarrierDetails) => {
          return numberFormat(ac.dailyMileage || 0, "km", 4);
        }
      },
      {
        id: "dailyPrice",
        label: "Dnevna cijena",
        dataName: "dailyPrice",
        dataFunc: (ac: AuthorizedCarrierDetails) => {
          return currencyFormat(ac.dailyPrice, financingRequest.currency)
        }
      },
      {
        id: "numberOfStudents",
        label: "Broj učenika na zahtjevu",
        dataName: "numberOfStudents"
      },
      {
        id: "numberOfStudentsTotal",
        label: "Broj učenika ukupno",
        dataName: "numberOfStudentsTotal"
      },
      {
        id: "documents",
        label: "Dokumentacija",
        dataFunc: (ac: AuthorizedCarrierDetails) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {location.pathname === financingRequestPrintUrl(id) && ac.uploadedDocuments && ac.uploadedDocuments.map((file, index) => (
              <div key={index} style={{ alignItems: 'center' }}>
                <p>{file.fileName}</p>
              </div>
            ))}
            {location.pathname !== financingRequestPrintUrl(id) && ac.uploadedDocuments && ac.uploadedDocuments.map((file, index) => (
              <div key={index} style={{ justifyContent: 'space-between', alignItems: 'center', display: 'grid' }}>
                <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "authorized", file.fileName || "document.pdf")}>
                  {file.fileName}
                </Link>
              </div>
            ))}
          </div>
        )
      }
    ];

  const authorizedCarrierTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: (financingRequest && financingRequest.authorizedCarriers) || []
  };

  const tableHeadMetadata: TrTableHeadMetadata<
    FinancingRequestStudentDetails
  >[] = [
      {
        id: "lastName",
        label: "Ime",
        dataName: "lastName",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              {d.lastName + " " + d.firstName}
            </Typography>
            <Typography variant="body2">{d.birthDate}</Typography>
          </div>
        )
      },
      { id: "oib", label: "OIB", dataName: "oib" },
      {
        id: "schoolClass",
        label: "Razred",
        dataName: "schoolClass",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">{d.programName}</Typography>
            <Typography variant="body2">
              {d.schoolClass + " - " + d.sectionMark}
            </Typography>
          </div>
        )
      },
      {
        id: "vrstaOdjeljenja",
        label: "Vrsta odjeljenja",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">{d.sectionTypeName}</Typography>
          </div>
        )
      },
      {
        id: "transportOption",
        label: "Prijevoz",
        dataName: "transportOption",
        dataFunc: (d: FinancingRequestStudentDetails) =>
          getTransportOptionInfoByStudentId(d.studentId, financingRequest.currency)
      },
      {
        id: "foodOption",
        label: "Prehrana",
        dataName: "foodOption",
        dataFunc: (d: FinancingRequestStudentDetails) =>
          getFoodOptionInfoByStudentId(d.studentId)
      },
      {
        id: "teachingAidOption",
        label: "Posebna nastavna sredstva i pomagala",
        dataName: "teachingAidOption",
        dataFunc: (d: FinancingRequestStudentDetails) =>
          getTeachingAidOptionInfoByStudentId(
            d.studentId,
            d.programType,
            d.specialNeedType,
            financingRequest.currency,
            annualAdministration

          )
      },
      {
        id: "validityRangeColumn",
        label: "Završni mjesec važenja troškova",
        dataName: "validTo",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              {d.validTo.charAt(0).toUpperCase() + d.validTo.slice(1)}
            </Typography>
            <Typography variant="body2">{d.validToReason}</Typography>
          </div>
        )
      },
      {
        id: "documents",
        label: "Dokumentacija",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <div style={{ justifyContent: 'space-between', alignItems: 'center', display: 'grid' }}>
            {location.pathname === financingRequestPrintUrl(id) ?
              d.uploadedDocuments && d.uploadedDocuments.map((file, index) => (
                <p>{file.fileName}</p>
              ))
              :
              d.uploadedDocuments && d.uploadedDocuments.map((file, index) => (
                <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "student", file.fileName || "document.pdf")}>
                  {file.fileName}
                </Link>
              ))
            }
          </div>
        )
      }
    ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "studentId",
    data: (financingRequest && financingRequest.studentDetails) || []
  };

  return financingRequest.id ? (
    <div className={classes.root}>
      {location.pathname !== financingRequestPrintUrl(id) ? (
      <Grid item xs={12}>
        <Button
          variant="outlined"
          color="primary"
          component={Link}
          to="/financing-requests"
          style={{ marginBottom: "16px" }}
        >
          {<ArrowBackIcon />}
        </Button>
      </Grid>
      ) : null}
      <Grid container spacing={2} style={{ display: 'flex' }}>
        <Grid item xs={12}>
          <Typography variant="h5">
            Zahtjev za sufinanciranjem - {financingRequest.schoolName}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Detalji zahtjeva"
              titleTypographyProps={{ variant: "h6" }}
              classes={{ title: classes.title }}
            />
            <Divider />
            <CardContent>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Školska godina
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.schoolYear}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Klasifikacijska oznaka
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.classificationMark}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Datum od kada vrijedi zahtjev
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.validFrom}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Broj učenika
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.studentsNumber}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Urudžbeni broj
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.registerNumber}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Datum do kada vrijedi zahtjev
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.validTo}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {financingRequest.decisionDocument ? (
          <Grid id='decision' item lg={12} style={{ display: 'block' }}>
            <Paper>
              <TrTable
                headMetadata={decisionTableHeadMetadata}
                bodyData={decisionTableBodyData}
              />
            </Paper>
          </Grid>
        ) : (
          <></>
        )}
        {financingRequest.schoolCarriers &&
          financingRequest.schoolCarriers.length > 0 ? (
          <Grid id='schoolCarriers' item lg={12}>
            <Paper>
              <TrTable
                headMetadata={schoolCarrierTableHeadMetadata}
                bodyData={schoolCarrierTableBodyData}
              />
            </Paper>
          </Grid>
        ) : (
          <></>
        )}
        {financingRequest.authorizedCarriers &&
          financingRequest.authorizedCarriers.length > 0 ? (
          <Grid item lg={12}>
            <Paper>
              <TrTable
                headMetadata={authorizedCarrierTableHeadMetadata}
                bodyData={authorizedCarrierTableBodyData}
              />
            </Paper>
          </Grid>
        ) : (
          <></>
        )}
        <Grid item lg={12} xl={12} style={{ display: 'flex' }}>
          <Paper>
            <TrTable
              headMetadata={tableHeadMetadata}
              bodyData={tableBodyData}
            />
          </Paper>
        </Grid>
        {location.pathname !== financingRequestPrintUrl(id) ? (
          <Grid item sm={12}>
            <Box textAlign="right">
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
              >
                <PictureAsPdf className={classes.icon} />
                Preuzmite zahtjev
              </Button>
            </Box>
          </Grid>
        ) : null}
      </Grid>
    </div>
  ) : null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1
    },
    title: {
      fontWeight: 400
    },
    icon: {
      marginRight: theme.spacing(1)
    }
  })
);
